import i18next from 'i18next';
import { SagaIterator } from 'redux-saga';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import {
  PapiDaSharedShippingProfileList,
  PapiDaSharedShippingProfileDestinations,
  PapiDaSharedShippingProfileUpdate,
  PapiDaSharedShippingProfileDelete,
  PapiRequestDaSharedShippingProfileUpdate,
} from '@wix/da-papi-types';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import {
  fetchShippingProfiles,
  fetchShippingProfilesSuccess,
  fetchShippingProfilesError,
  fetchShippingDestinations,
  fetchShippingDestinationsSuccess,
  fetchShippingDestinationsError,
  saveShippingProfile,
  saveShippingProfileSuccess,
  saveShippingProfileError,
  deleteShippingProfile,
  deleteShippingProfileSuccess,
  deleteShippingProfileError,
} from '../actions/shippingProfiles';

const IN_USE_ERROR_KEY = 'profile_in_use';

function* fetchShippingProfilesSaga(): SagaIterator {
  const response: PapiDaSharedShippingProfileList = yield call(requestPuppy, {
    method: 'get',
    url: '/shipping_profile/list',
  });

  if (!response || isApiError(response)) {
    yield put(fetchShippingProfilesError());
    yield call(putErrorNotification, i18next.t('manageShippingProfiles.error.fetch'));
    return;
  }

  yield put(
    fetchShippingProfilesSuccess({
      profiles: response.profiles.map(p => ({
        shippingProfileid: p.shippingProfileid,
        title: p.title,
        rates: p.rates.map(r => ({
          destinationType: r.destinationType,
          destinationId: r.destinationId,
          priceUsd: r.priceUsd ?? '0',
        })),
      })),
    })
  );
}

function* fetchShippingDestinationsSaga(): SagaIterator {
  const response: PapiDaSharedShippingProfileDestinations = yield call(requestPuppy, {
    method: 'get',
    url: '/shipping_profile/destinations',
  });

  if (!response || isApiError(response)) {
    yield put(fetchShippingDestinationsError());
    yield call(putErrorNotification, i18next.t('manageShippingProfiles.error.fetch'));
    return;
  }

  yield put(fetchShippingDestinationsSuccess({ destinations: response.destinations }));
}

function* saveShippingProfileSaga(action: ReturnType<typeof saveShippingProfile>): SagaIterator {
  const { title, rates, profileId, onSuccess, onError } = action.payload;

  const data: PapiRequestDaSharedShippingProfileUpdate = {
    shipping_profileid: profileId,
    title,
    rates: rates.reduce<Record<string, any>>((acc, rate, index) => {
      acc[index] = {
        destination_type: rate.destinationType,
        destination_id: rate.destinationId,
        price_usd: rate.priceUsd,
      };
      return acc;
    }, {}),
  };

  const response: PapiDaSharedShippingProfileUpdate = yield call(requestPuppy, {
    method: 'post',
    url: '/shipping_profile/update',
    data,
  });

  if (!response || isApiError(response)) {
    yield put(saveShippingProfileError());
    yield call(putErrorNotification, i18next.t('manageShippingProfiles.error.save'));
    if (onError) {
      yield call(onError);
    }
    return;
  }

  yield put(saveShippingProfileSuccess());

  /* Re-fetch the list so IDs and state are up-to-date after create/update */
  yield put(fetchShippingProfiles());

  if (onSuccess) {
    yield call(onSuccess);
  }
}

function* deleteShippingProfileSaga(
  action: ReturnType<typeof deleteShippingProfile>
): SagaIterator {
  const { profileId, onSuccess, onError } = action.payload;

  const response: PapiDaSharedShippingProfileDelete = yield call(requestPuppy, {
    method: 'post',
    url: '/shipping_profile/delete',
    data: { shipping_profileid: profileId },
  });

  if (!response || isApiError(response)) {
    const isInUse =
      response &&
      'errorDetails' in response &&
      (response as any).errorDetails?.errorKey === IN_USE_ERROR_KEY;

    yield put(deleteShippingProfileError({ profileId, isInUse }));

    if (!isInUse) {
      yield call(putErrorNotification, i18next.t('manageShippingProfiles.error.delete'));
    }

    if (onError) {
      yield call(onError, isInUse);
    }
    return;
  }

  yield put(deleteShippingProfileSuccess({ profileId }));

  if (onSuccess) {
    yield call(onSuccess);
  }
}

export default function* shippingProfilesSaga() {
  yield all([
    takeEvery(fetchShippingProfiles, fetchShippingProfilesSaga),
    takeEvery(fetchShippingDestinations, fetchShippingDestinationsSaga),
    takeEvery(saveShippingProfile, saveShippingProfileSaga),
    takeEvery(deleteShippingProfile, deleteShippingProfileSaga),
  ]);
}
